/**
 * receiptEligibilityFlags (bk-017) — the SINGLE bounded fetcher behind the OPD
 * board's "Refund applicable" label. Given the visible visits it issues ONE
 * batch eligibility call per page of up to BATCH_MAX_ITEMS (C-6) — never one call
 * per row — and dedups against a caller-owned `checkedSet` so re-renders / repeat
 * calls can't multiply requests (single-flight at the id grain).
 *
 * Pure orchestration (no React) so the request count is directly unit-testable:
 * N visits → ceil(N / BATCH_MAX_ITEMS) calls, and already-checked ids → 0 calls.
 */
import { fetchReceiptEligibilityBatch, BATCH_MAX_ITEMS } from '../../services/api/receiptsApi';

/**
 * @param {Array<{visit_id:string, patient_id?:string}>} visits  visible visits
 * @param {{ checkedSet: Set<string> }} opts  ids already fetched (mutated: pending ids added; failures removed)
 * @returns {Promise<Object<string, boolean>>}  visit_id → refund-applicable flag (only newly-checked ids)
 */
export async function fetchRefundFlags(visits, { checkedSet } = {}) {
  const seen = checkedSet || new Set();
  const pending = (Array.isArray(visits) ? visits : [])
    .filter((v) => v && v.visit_id && !seen.has(v.visit_id));
  if (!pending.length) return {};
  pending.forEach((v) => seen.add(v.visit_id));

  const flags = {};
  for (let i = 0; i < pending.length; i += BATCH_MAX_ITEMS) {
    const chunk = pending.slice(i, i + BATCH_MAX_ITEMS);
    const items = chunk.map((v) => ({ visit_id: v.visit_id, patient_id: v.patient_id }));
    // eslint-disable-next-line no-await-in-loop
    const res = await fetchReceiptEligibilityBatch(items);
    if (!res.success) { chunk.forEach((v) => seen.delete(v.visit_id)); continue; } // allow a retry
    const results = res.data?.results || {};
    chunk.forEach((v) => {
      const payments = Array.isArray(results[v.visit_id]?.payments) ? results[v.visit_id].payments : [];
      flags[v.visit_id] = payments.some(
        (p) => p?.source_type !== 'REFUND' && p?.refund_eligible
          && Number(p?.refundable_remaining_paise || 0) > 0,
      );
    });
  }
  return flags;
}
